import React, { useState, useEffect } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { useToast, Spinner, ErrorBanner } from '../../components/ui';
import { errMsg } from '../../utils/format';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function Settings() {
  const { isAdmin, loadSettings } = useAuth();
  const { data, loading, error, refetch } = useFetch('/api/settings');
  const { showToast, Toast } = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch('/api/settings', form);
      showToast('Settings saved successfully');
      await loadSettings();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;
  if (!isAdmin) return <div className="alert alert-info" style={{ marginTop:16 }}><i className="ti ti-lock" /> Only admin users can manage settings.</div>;

  return (
    <div>

      <div className="page-header"><h1>Settings</h1></div>
      <form onSubmit={save}>
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Inventory</div>
          <div className="form-group">
            <label>Days of cover lookback period</label>
            <select value={form.days_of_cover_lookback_days||'30'} onChange={e => set('days_of_cover_lookback_days', e.target.value)}>
              <option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option><option value="60">60 days</option><option value="90">90 days</option>
            </select>
          </div>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Raw material deduction</div>
          <p style={{ fontSize:12, color:'var(--text-tertiary)', marginBottom:12 }}>
            Control when raw materials are automatically deducted from inventory. You can enable one or both methods — or neither if you manage raw material quantities manually.
          </p>
          <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <input type="checkbox" id="deduct-wo" style={{ width:'auto' }}
              checked={form.deduct_materials_on_work_order !== 'false'}
              onChange={e => set('deduct_materials_on_work_order', e.target.checked ? 'true' : 'false')} />
            <label htmlFor="deduct-wo" style={{ marginBottom:0 }}>
              Deduct raw materials when a work order is <strong>started</strong>
            </label>
          </div>
          <small style={{ fontSize:11, color:'var(--text-tertiary)', marginLeft:26, display:'block', marginBottom:12 }}>
            Recommended for batch production. Materials are deducted when a work order moves from Planned to In Progress.
          </small>
          <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <input type="checkbox" id="deduct-shopify" style={{ width:'auto' }}
              checked={form.deduct_materials_on_shopify_sync === 'true'}
              onChange={e => set('deduct_materials_on_shopify_sync', e.target.checked ? 'true' : 'false')} />
            <label htmlFor="deduct-shopify" style={{ marginBottom:0 }}>
              Deduct raw materials when a <strong>Shopify order</strong> syncs
            </label>
          </div>
          <small style={{ fontSize:11, color:'var(--text-tertiary)', marginLeft:26, display:'block' }}>
            Recommended for made-to-order businesses. Materials are deducted when a fulfilled Shopify order is pulled in. Do not enable both methods or materials will be double-deducted.
          </small>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Feature flags</div>
          <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <input type="checkbox" id="backorders" style={{ width:'auto' }}
              checked={form.enable_backorders !== 'false'}
              onChange={e => set('enable_backorders', e.target.checked ? 'true' : 'false')} />
            <label htmlFor="backorders" style={{ marginBottom:0 }}>Enable backorders module</label>
          </div>
          <div className="form-group" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <input type="checkbox" id="workorders" style={{ width:'auto' }}
              checked={form.enable_work_orders !== 'false'}
              onChange={e => set('enable_work_orders', e.target.checked ? 'true' : 'false')} />
            <label htmlFor="workorders" style={{ marginBottom:0 }}>Enable work orders module</label>
          </div>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Shopify</div>
          <p style={{ fontSize:12, color:'var(--text-tertiary)', marginBottom:0 }}>
            Shopify store accounts are now managed on the <strong>Integrations</strong> page. Go there to add, edit, or remove stores and configure credentials.
          </p>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Alerts</div>
          <div className="form-group">
            <label>Alert email recipients (comma-separated)</label>
            <input
              value={form._alert_recipients_raw !== undefined
                ? form._alert_recipients_raw
                : JSON.parse(form.alert_recipients||'[]').join(', ')}
              onChange={e => setForm(f => ({
                ...f,
                _alert_recipients_raw: e.target.value,
                alert_recipients: JSON.stringify(
                  e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                )
              }))}
              placeholder="e.g. todd@summitfishingequipment.com, jane@summitfishingequipment.com"
            />
            <small style={{ fontSize:11, color:'var(--text-tertiary)' }}>
              Separate multiple addresses with a comma
            </small>
          </div>
          <div className="form-group"><label>Slack webhook URL (optional)</label><input value={form.alert_slack_webhook||''} onChange={e => set('alert_slack_webhook', e.target.value)} /></div>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Purchase orders</div>
          <div className="form-group"><label>Approval required above ($0 = disabled)</label><input type="number" min="0" value={form.po_approval_threshold||'0'} onChange={e => set('po_approval_threshold', e.target.value)} /></div>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Returns</div>
          <div className="form-group"><label>Default return window (days)</label><input type="number" min="1" value={form.default_return_window_days||'30'} onChange={e => set('default_return_window_days', e.target.value)} /></div>
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Source Code</div>
          <p style={{ fontSize:12, color:'var(--text-tertiary)', marginBottom:12 }}>
            Download a zip of the current server and client source code for backup or to share with Claude for development.
          </p>
          <DownloadSourceButton />
        </div>

        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-title">Database Maintenance</div>
          <p style={{ fontSize:12, color:'var(--text-tertiary)', marginBottom:12 }}>
            Records older than these thresholds are automatically purged daily at 3:00 AM.
            Set to 0 to disable purging for that table.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label>Shopify write logs (days)</label>
              <input type="number" min="0" value={form.retention_sync_events_writes_days||'30'} onChange={e => set('retention_sync_events_writes_days', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Sync logs (days)</label>
              <input type="number" min="0" value={form.retention_sync_events_syncs_days||'90'} onChange={e => set('retention_sync_events_syncs_days', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Inventory adjustments (days)</label>
              <input type="number" min="0" value={form.retention_inventory_adjustments_days||'365'} onChange={e => set('retention_inventory_adjustments_days', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Alert log (days)</label>
              <input type="number" min="0" value={form.retention_alert_log_days||'90'} onChange={e => set('retention_alert_log_days', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Activity log (days)</label>
              <input type="number" min="0" value={form.retention_activity_log_days||'90'} onChange={e => set('retention_activity_log_days', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Resolved Shopify push queue (days)</label>
              <input type="number" min="0" value={form.retention_pending_pushes_resolved_days||'30'} onChange={e => set('retention_pending_pushes_resolved_days', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Sales orders (days)</label>
              <input type="number" min="0" value={form.retention_sales_orders_days||'1095'} onChange={e => set('retention_sales_orders_days', e.target.value)} />
            </div>
          </div>
          <button type="button" className="btn btn-sm" style={{ marginTop:4 }} onClick={async () => {
            if (!window.confirm('Run database maintenance now? This will delete old records immediately.')) return;
            try {
              await api.post('/api/integrations/db-maintenance');
              alert('Database maintenance started — check sync log for results.');
            } catch (err) { alert(errMsg(err)); }
          }}>
            <i className="ti ti-database" /> Run maintenance now
          </button>
        </div>

        <button className="btn btn-primary" type="submit" disabled={saving}>{saving?'Saving…':'Save settings'}</button>
      </form>

      {/* Shipping / HazMat Settings */}
      <ShippingSettings />
    </div>
  );
}

function ShippingSettings() {
  const { data, refetch } = useFetch('/api/shipping/settings');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/api/shipping/settings', form);
      showToast('Shipping settings saved');
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setSaving(false); }
  };

  const Field = ({ k, label, type = 'text', placeholder }) => (
    <div className="form-group">
      <label>{label}</label>
      <input type={type} value={form[k] || ''} placeholder={placeholder}
        onChange={e => set(k, e.target.value)} />
    </div>
  );

  return (
    <div className="card" style={{ marginTop:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div className="card-title">Shipping &amp; HazMat Settings</div>
        <a href="/settings/hazmat-form" className="btn btn-sm">
          <i className="ti ti-layout" /> HazMat Form Editor
        </a>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:8 }}>FedEx API</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div className="form-group">
            <label>Environment</label>
            <select value={form.fedex_environment || 'sandbox'} onChange={e => set('fedex_environment', e.target.value)}>
              <option value="sandbox">Sandbox (test)</option>
              <option value="production">Production (live)</option>
            </select>
          </div>
          <Field k="fedex_account_number"  label="Production Account Number" />
          <Field k="fedex_client_id"       label="Client ID" />
          <Field k="fedex_client_secret"   label="Client Secret" type="password" />
          <Field k="fedex_sandbox_account" label="Sandbox Account Number" placeholder="740980114" />
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:8 }}>UPS API</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div className="form-group">
            <label>Environment</label>
            <select value={form.ups_environment || 'sandbox'} onChange={e => set('ups_environment', e.target.value)}>
              <option value="sandbox">Sandbox (test)</option>
              <option value="production">Production (live)</option>
            </select>
          </div>
          <Field k="ups_account_number" label="UPS Account Number" />
          <Field k="ups_client_id"     label="Client ID" />
          <Field k="ups_client_secret" label="Client Secret" type="password" />
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:8 }}>Shipper Info</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field k="ups_shipper_name"     label="Company Name" />
          <Field k="ups_shipper_phone"    label="Phone" />
          <Field k="ups_shipper_address1" label="Address" />
          <Field k="ups_shipper_city"     label="City" />
          <Field k="ups_shipper_state"    label="State" placeholder="OH" />
          <Field k="ups_shipper_zip"      label="ZIP" />
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:8 }}>Emergency Response</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field k="ups_er_contact_name"      label="Contact Name" />
          <Field k="ups_er_contact_number"    label="Contact Phone" />
          <Field k="ups_er_registrant_ground" label="ER Registrant (Ground)" />
          <Field k="ups_er_registrant_air"    label="ER Registrant (Air)" />
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontWeight:500, fontSize:13, marginBottom:8 }}>Printers</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field k="zebra_printer_ip"      label="Zebra ZT231 IP Address" placeholder="192.168.1.139" />
          <Field k="zebra_printer_port"    label="Zebra ZT231 Port" placeholder="9100" />
          <Field k="zebra_printer_name"    label="Zebra Printer Name (QZ Tray)" placeholder="ZDesigner ZT231" />
          <Field k="hazmat_printer_name"   label="HazMat Form Printer (QZ Tray)" placeholder="HAZMAT Printer" />
          <Field k="manifest_printer_name" label="Manifest Printer (QZ Tray)" placeholder="Brother MFC-L8900CDW series Printer" />
        </div>
      </div>

      <button className="btn btn-primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save shipping settings'}
      </button>
    </div>
  );
}

function DownloadSourceButton() {
  const [dl, setDl] = useState(false);
  return (
    <button type="button" className="btn btn-sm" disabled={dl} onClick={async () => {
      setDl(true);
      try {
        const resp = await fetch('/api/settings/source-download', {
          headers: { Authorization: `Bearer ${localStorage.getItem('sf_token')}` }
        });
        if (!resp.ok) throw new Error(await resp.text());
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stockforge-source-${new Date().toISOString().slice(0,10).replace(/-/g,'')}.zip`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
      } catch (err) { alert('Download failed: ' + err.message); }
      finally { setDl(false); }
    }}>
      {dl ? <><span style={{width:11,height:11,border:'2px solid rgba(0,0,0,0.2)',borderTopColor:'#555',borderRadius:'50%',animation:'spin 0.7s linear infinite',display:'inline-block',marginRight:6}}/> Generating&hellip;</> : <><i className="ti ti-download" /> Download Source Zip</>}
    </button>
  );
}
